import { useState, useEffect, useCallback } from 'react';
import useProtobufCollection from '../ble/protobufService/useProtobufCollection';

// MapPoint protobuf schema constraints
const MAP_POINT_CONSTRAINTS = {
  NAME_MAX_LENGTH: 16,
  MAX_COUNT: 700
};

// Interactivity enum values
const INTERACTIVITY = {
  NONE: 0,
  BT_BEACON: 1
};

// Byte-aware string truncation for UTF-8 encoding
const truncateStringToBytes = (str, maxBytes) => {
  if (!str) return '';
  
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  
  if (bytes.length <= maxBytes) {
    return str;
  }
  
  // Find the last complete character that fits within maxBytes
  let truncatedBytes = bytes.slice(0, maxBytes);
  let truncatedString = new TextDecoder().decode(truncatedBytes);
  
  // If the last character was cut off, remove it
  if (encoder.encode(truncatedString).length > maxBytes) {
    truncatedBytes = bytes.slice(0, maxBytes - 1);
    truncatedString = new TextDecoder().decode(truncatedBytes);
  }
  
  return truncatedString;
};

export default function useMapCollection(statusUuid, recordUuid, protobufRoot, protobufProtocolRoot, protobufProtocolService) {
  const [localPoints, setLocalPoints] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);

  const {
    collectionStatus,
    records,
    loading,
    error,
    collectionLoaded,
    loadingProgress,
    initialized,
    clearCollection,
    loadCollection,
    addItem,
    deleteLastItem,
    updateRecord,
    finishTransaction,
    clearError,
    uploadCollection,
    loadCollectionStatus // <-- add this line to destructure from useProtobufCollection
  } = useProtobufCollection(statusUuid, recordUuid, 'MapPoint', protobufRoot, protobufProtocolRoot, protobufProtocolService);

  // Sync local points with collection records
  useEffect(() => {
    if (records.length > 0) {
      // Convert collection records to local points format
      const points = records.map(record => ({
        id: record.id || '',
        name: record.name || '',
        coordinates: {
          latitude: record.coordinates?.latitude || 0,
          longitude: record.coordinates?.longitude || 0
        },
        radius: record.radius || 0,
        interactivity: record.interactivity || 0,
        visited: record.visited || false,
        start: record.start || 0,
        end: record.end || 0,
        rating: record.rating || 0,
        uid: record.uid || ''
      }));
      setLocalPoints(points);
    } else if (collectionLoaded && records.length === 0) {
      setLocalPoints([]);
    }
  }, [records, collectionLoaded]);

  // Upload local points to collection
  const uploadPointsToCollection = useCallback(async (points, progressCallback, finishTransaction) => {
    if (!initialized) {
      throw new Error('Collection service not initialized');
    }

    setIsSyncing(true);
    try {
      // Convert points to records format with name truncation and validation
      const records = points.map((point, index) => {
        const originalName = point.name;
        const truncatedName = truncateStringToBytes(originalName, MAP_POINT_CONSTRAINTS.NAME_MAX_LENGTH);
        
        if (originalName && truncatedName !== originalName) {
          
          // eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: pre-existing write-only/unused binding in the legacy BLE+compass tree. Removing it is a behaviour question, not a lint fix; suppressed explicitly so no-unused-vars can stay an ERROR everywhere else.
          const truncatedBytes = new TextEncoder().encode(truncatedName).length;
        }
        
        // Validate and convert data types according to protobuf schema
        const record = {
          id: Number(point.id) || 0, // uint32
          name: truncatedName, // string, max 16 chars
          coordinates: {
            latitude: Number(point.coordinates?.latitude) || 0, // double
            longitude: Number(point.coordinates?.longitude) || 0 // double
          },
          radius: Number(point.radius) || 0, // uint32
          interactivity: Number(point.interactivity) || INTERACTIVITY.NONE, // enum (0 = NONE, 1 = BT_BEACON)
          visited: Boolean(point.visited), // bool
          start: Number(point.start) || 0, // uint32
          end: Number(point.end) || 0, // uint32
          rating: Number(point.rating) || 0, // uint32
          uid: point.uid || ''
        };
        
        // Log any data conversion issues
        if (isNaN(record.id) || isNaN(record.radius) || isNaN(record.start) || isNaN(record.end) || isNaN(record.rating)) {
          console.warn(`⚠️ Data conversion issue for point ${index}:`, point);
        }
        
        return record;
      });
      
      console.log(`📤 Uploading ${records.length} records to device...`);
      console.log('📋 First record:', records[0]);
      
      // Use the new uploadCollection method
      await uploadCollection(records, (progress) => {
        if (progressCallback) {
          progressCallback(progress.current, progress.total);
        }
      }, finishTransaction);
      
      return true;
    } catch (error) {
      console.error('Error uploading points to collection:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  }, [initialized, uploadCollection]);

  // Download points from collection
  const downloadPointsFromCollection = useCallback(async (progressCallback) => {
    if (!initialized) {
      throw new Error('Collection service not initialized');
    }

    setIsSyncing(true);
    try {
      // Clear existing local points before starting download
      setLocalPoints([]);
      
      // Pass the progress callback to loadCollection
      await loadCollection((progress) => {
        if (progressCallback) {
          progressCallback(progress.current, progress.total);
        }
      });
      
      // The useEffect above will handle syncing the points in real-time as records are received
      
      return true;
    } catch (error) {
      console.error('Error downloading points from collection:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  }, [initialized, loadCollection]);

  // Update a point in the collection
  const updatePointInCollection = useCallback(async (index, updatedPoint) => {
    if (!initialized) {
      throw new Error('Collection service not initialized');
    }

    try {
      const record = {
        id: updatedPoint.id,
        name: updatedPoint.name,
        coordinates: {
          latitude: updatedPoint.coordinates.latitude,
          longitude: updatedPoint.coordinates.longitude
        },
        radius: updatedPoint.radius,
        interactivity: updatedPoint.interactivity,
        visited: updatedPoint.visited,
        start: updatedPoint.start,
        end: updatedPoint.end,
        rating: updatedPoint.rating,
        uid: updatedPoint.uid
      };
      
      await updateRecord(index, record);
      return true;
    } catch (error) {
      console.error('Error updating point in collection:', error);
      throw error;
    }
  }, [initialized, updateRecord]);

  // Add a point to the collection
  const addPointToCollection = useCallback(async (point) => {
    if (!initialized) {
      throw new Error('Collection service not initialized');
    }

    try {
      const record = {
        id: point.id,
        name: point.name,
        coordinates: {
          latitude: point.coordinates.latitude,
          longitude: point.coordinates.longitude
        },
        radius: point.radius,
        interactivity: point.interactivity,
        visited: point.visited,
        start: point.start,
        end: point.end,
        rating: point.rating,
        uid: point.uid
      };
      
      await addItem(record);
      return true;
    } catch (error) {
      console.error('Error adding point to collection:', error);
      throw error;
    }
  }, [initialized, addItem]);

  // Delete last point from collection
  const deleteLastPointFromCollection = useCallback(async () => {
    if (!initialized) {
      throw new Error('Collection service not initialized');
    }

    try {
      await deleteLastItem();
      return true;
    } catch (error) {
      console.error('Error deleting last point from collection:', error);
      throw error;
    }
  }, [initialized, deleteLastItem]);

  return {
    // Collection state
    collectionStatus,
    records,
    loading,
    error,
    collectionLoaded,
    loadingProgress,
    initialized,
    
    // Local points state
    localPoints,
    setLocalPoints,
    isSyncing,
    
    // Collection operations
    clearCollection,
    loadCollection,
    addItem,
    deleteLastItem,
    updateRecord,
    finishTransaction,
    clearError,
    
    // Bridged operations
    uploadPointsToCollection,
    downloadPointsFromCollection,
    updatePointInCollection,
    addPointToCollection,
    deleteLastPointFromCollection,
    loadCollectionStatus // <-- expose this method
  };
} 